/**
 * Phone Number Input Component
 * An input field for phone numbers with country code support using react-international-phone
 */

import { useEffect, useMemo, useState } from "react"
import { PhoneInput } from "react-international-phone"

import "react-international-phone/style.css"

import { Label } from "@/components/ui/label"
import { cn } from "@/lib"

interface PhoneInputProps {
  label?: string
  required?: boolean
  error?: string
  countryCode?: string
  onCountryCodeChange?: (code: string) => void
  onPhoneNumberChange?: (phone: string) => void
  disabled?: boolean
  placeholder?: string
}

export function PhoneNumberInput({
  label,
  required,
  error,
  countryCode = "+1",
  onCountryCodeChange,
  onPhoneNumberChange,
  disabled = false,
  placeholder = "Enter phone number",
}: PhoneInputProps) {
  // Combine countryCode and phoneNumber into a single value for the library
  const [phoneValue, setPhoneValue] = useState("")

  // Build the full phone value when countryCode changes externally
  useEffect(() => {
    if (countryCode && phoneValue) {
      // If the current phoneValue doesn't start with the country code, rebuild it
      const currentDialCode = phoneValue.replace(/\D/g, "").slice(0, 4)
      const newDialCode = countryCode.replace("+", "")
      if (!phoneValue.startsWith(countryCode) && currentDialCode !== newDialCode) {
        // Preserve the phone number portion but update the country code
        const currentPhoneNumber = phoneValue.replace(/^\+\d+\s*/, "")
        setPhoneValue(`${countryCode} ${currentPhoneNumber}`)
      }
    } else if (countryCode && !phoneValue) {
      setPhoneValue(`${countryCode} `)
    }
  }, [countryCode, phoneValue])

  const handleChange = (
    phone: string,
    meta: { country?: { dialCode?: string; iso2?: string } },
  ) => {
    setPhoneValue(phone)

    // Extract country code from the phone value
    const dialCode = meta.country?.dialCode
    if (dialCode) {
      onCountryCodeChange?.(`+${dialCode}`)
    }

    // Strip country code prefix from phone number before passing to form
    const countryDigits = dialCode || countryCode.replace("+", "")
    let phoneWithoutCountryCode = phone
    if (countryDigits) {
      phoneWithoutCountryCode = phone.replace(`+${countryDigits}`, "").trim()
    }
    onPhoneNumberChange?.(phoneWithoutCountryCode)
  }

  // Style configuration for underline style matching CustomInput
  const inputStyle = useMemo(
    () => ({
      flex: 1,
      borderTop: 0,
      borderRight: 0,
      borderLeft: 0,
      borderBottom: error
        ? "1px solid var(--color-brand-terracotta-bright)"
        : "1px solid var(--color-input)",
      borderRadius: 0,
      boxShadow: "none",
      outline: "none",
      fontSize: "0.875rem",
      fontWeight: 600,
      transition: "all 0.2s",
      paddingLeft: "8px",
      backgroundColor: "transparent",
      color: "inherit",
    }),
    [error],
  )

  const countrySelectorStyleProps = useMemo(
    () => ({
      buttonStyle: {
        border: 0,
        borderBottom: error
          ? "1px solid var(--color-brand-terracotta-bright)"
          : "1px solid var(--color-input)",
        borderRadius: 0,
        padding: "1rem 0.5rem",
        flexShrink: 0,
        fontSize: "0.875rem",
        fontWeight: 600,
        transition: "all 0.2s",
        backgroundColor: "transparent",
      },
      flagStyle: {
        width: "20px",
        height: "14px",
      },
      dropdownStyleProps: {
        style: {
          backgroundColor: "#000",
          border: "none",
          borderRadius: "0.375rem",
          color: "#fff",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          maxHeight: "320px",
          overflowY: "auto" as const,
          zIndex: 50,
          marginTop: "4px",
        },
        listItemStyle: {
          padding: "12px 16px",
          fontSize: "0.875rem",
          border: "none",
        },
      },
    }),
    [error],
  )

  return (
    <div className="space-y-0 md:w-[362px] flex flex-col gap-1.25">
      {label && (
        <Label
          className={cn(
            "text-xs leading-5 uppercase",
            error ? "text-brand-terracotta-bright" : "text-body",
          )}
        >
          {label}
          {required && (
            <span className={cn("", error ? "text-brand-terracotta-bright" : "text-body")}>*</span>
          )}
        </Label>
      )}
      <div className={cn("phone-input-wrapper", error && "has-error")}>
        <PhoneInput
          value={phoneValue}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          defaultCountry="us"
          inputStyle={inputStyle}
          countrySelectorStyleProps={countrySelectorStyleProps}
          inputProps={{
            name: "phoneNumber",
            required,
            autoFocus: false,
          }}
          forceDialCode={true}
          disableCountryGuess={true}
          className={cn(
            "w-full flex items-stretch border-0 border-b-1",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        />
      </div>
      {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
    </div>
  )
}
